import React from "react";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

interface CalendarViewProps {
    currentMonth: CalendarDate;
    selectedDates: Set<string>;
    setCurrentMonth: (date: CalendarDate) => void;
}

interface CalendarDay {
    date: CalendarDate;
    isInCurrentMonth: boolean;
}

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CalendarView: React.FC<CalendarViewProps> = ({
    currentMonth,
    selectedDates,
    setCurrentMonth,
}) => {
    const { t } = useTranslation('readyRoster');

    const calendarDateToLocalDate = (date: CalendarDate): string => {
        return `${date.year}-${String(date.month).padStart(2, "0")}-${String(
            date.day
        ).padStart(2, "0")}`;
    };

    const isDateSelected = (date: CalendarDate): boolean => {
        const dateStr = calendarDateToLocalDate(date);
        return selectedDates.has(dateStr);
    };

    const isPastDate = (date: CalendarDate): boolean => {
        return date.compare(today(getLocalTimeZone())) < 0;
    };

    const generateCalendarDays = (): CalendarDay[] => {
        const year = currentMonth.year;
        const month = currentMonth.month;
        const firstDay = new CalendarDate(year, month, 1);
        const lastDay = firstDay.add({ months: 1 }).subtract({ days: 1 });
        const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
        const days: CalendarDay[] = [];

        for (let i = firstDayOfWeek; i > 0; i--) {
            days.push({ date: firstDay.subtract({ days: i }), isInCurrentMonth: false });
        }
        for (let day = 1; day <= lastDay.day; day++) {
            days.push({ date: new CalendarDate(year, month, day), isInCurrentMonth: true });
        }

        let extraDays = 1;
        while (days.length % 7 !== 0) {
            days.push({ date: lastDay.add({ days: extraDays }), isInCurrentMonth: false });
            extraDays++;
        }

        return days;
    };

    const goToPreviousMonth = () => {
        setCurrentMonth(currentMonth.subtract({ months: 1 }));
    };

    const goToNextMonth = () => {
        setCurrentMonth(currentMonth.add({ months: 1 }));
    };

    const formatMonthYear = (date: CalendarDate): string => {
        return `${MONTH_NAMES[date.month - 1]} ${date.year}`;
    };

    const getDayClass = ({ date, isInCurrentMonth }: CalendarDay): string => {
        if (!isInCurrentMonth) return "text-gray-500 opacity-50";
        if (isDateSelected(date)) return "bg-blue-500 text-white";
        if (date.compare(today(getLocalTimeZone())) === 0) return "bg-gray-100 text-gray-900 font-bold";
        if (isPastDate(date)) return "cursor-not-allowed text-gray-300";
        return "text-gray-900";
    };

    const calendarDays = generateCalendarDays();

    return (
        <div className="flex w-full flex-col gap-2 font-['Plus_Jakarta_Sans',sans-serif]">
            <div className="flex items-center justify-between">
                <h4 className="text-[12px] font-normal leading-[16px] text-gray-500">
                    {t("available_dates")}
                </h4>
                {selectedDates.size > 0 && (
                    <div className="rounded bg-blue-50 px-2 text-[12px] font-medium leading-[16px] text-blue-600">
                        {t(
                            selectedDates.size === 1
                                ? "date_selected"
                                : "dates_selected",
                            { count: selectedDates.size }
                        )}
                    </div>
                )}
            </div>

            <div className="w-full rounded-3xl bg-white px-4 pb-4 xl:w-72">
                <div className="flex min-h-14 items-center justify-between py-4">
                    <h4 className="flex-1 text-[14px] font-medium leading-[20px] text-gray-900">
                        {formatMonthYear(currentMonth)}
                    </h4>
                    <button
                        onClick={goToPreviousMonth}
                        className="flex h-6 w-6 items-center justify-center rounded-md text-blue-500 hover:bg-gray-100"
                        type="button"
                    >
                        <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={goToNextMonth}
                        className="flex h-6 w-6 items-center justify-center rounded-md text-blue-500 hover:bg-gray-100"
                        type="button"
                    >
                        <ChevronRightIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="grid grid-cols-7 justify-items-center py-2">
                    {WEEK_DAYS.map((day) => (
                        <div
                            key={day}
                            className="flex w-9 justify-center text-[14px] font-medium leading-[20px] text-gray-500"
                        >
                            {day}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 justify-items-center gap-y-0.5 py-2">
                    {calendarDays.map((day) => (
                        <div
                            key={calendarDateToLocalDate(day.date)}
                            className={`flex h-9 w-9 items-center justify-center rounded-3xl text-[14px] font-medium leading-[20px] ${getDayClass(
                                day
                            )}`}
                        >
                            {day.date.day}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CalendarView;